import { IcColorStatusSActive } from '@assets/icons/24x24';
import { For, Grid, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { statusColorFromSensorStatus } from '@components/cockpit/panels/monitoring/monitoring.utils';
import { ColorLabelEnum, hexColor } from '@utils/map/map.constants';
import { FormattedMessage } from 'react-intl';

import { AutomatonCabinet, AutomatonCabinetStateEnum, AutomatonCabinetStatus } from '@/types/sensor/sensors';

const cabinetStateColor: Partial<Record<AutomatonCabinetStateEnum, string>> = {
  [AutomatonCabinetStateEnum.OK]: hexColor[ColorLabelEnum.GREEN],
  [AutomatonCabinetStateEnum.SLEEP]: hexColor[ColorLabelEnum.GRAY],
  [AutomatonCabinetStateEnum.TECHNICAL_DEFECT]: hexColor[ColorLabelEnum.ORANGE],
};

function CabinetNode(name: string, state: AutomatonCabinetStateEnum | null) {
  const color = state ? cabinetStateColor[state] : null;
  return (
    <HStack paddingY="7px" width="100%" gap={6}>
      <Text color="neutral.300">
        <FormattedMessage id={`automatonCabinet.value.${name}`} />
      </Text>
      {color ? (
        <Icon marginLeft="auto" flexShrink={0} boxSize="16px" color={color} as={IcColorStatusSActive} />
      ) : (
        <Text marginLeft="auto" flexShrink={0}>
          <FormattedMessage id="global.NA" />
        </Text>
      )}
    </HStack>
  );
}

function AutomatonCabinetNodes({
  status,
  nodes,
}: Readonly<{ status?: AutomatonCabinetStatus | null; nodes: string[] }>) {
  return (
    <VStack
      paddingX={9}
      paddingY={4}
      backgroundColor="neutral.700"
      gap={1}
      width="100%"
      height="100%"
      alignItems="start"
    >
      <Text paddingY="11px" fontWeight="medium" color="neutral.200" textTransform="uppercase">
        <FormattedMessage id="components.monitoring.sensorDetail.global.automaton.node" />
      </Text>
      <For each={nodes}>
        {(node) => {
          const state = Object.values(AutomatonCabinetStateEnum).find((k) => status?.cabinetStates[k]?.includes(node));
          return CabinetNode(node, state ?? null);
        }}
      </For>
    </VStack>
  );
}

export function AutomatonCabinetSection({
  configuration,
  status,
}: Readonly<{ configuration: AutomatonCabinet; status?: AutomatonCabinetStatus | null }>) {
  return (
    <Grid
      key={`automaton-cabinet-${configuration.name}`}
      width="100%"
      height="min-content"
      gap={0.25}
      backgroundColor="neutral.800"
      templateColumns="repeat(3, 1fr)"
    >
      <AutomatonCabinetNodes
        status={status}
        nodes={[
          'Alarmes.Alarme_Tech_Armoire',
          'Coffret.Alarme_Generale',
          'Coffret.Arret_Urgence',
          'Coffret.Auto_protection',
          'Coffret.Auto_protection_armoire',
          'Coffret.Defaut_Disj_2nd_terre',
          'Coffret.Defaut_Disj_2nd_thermique',
          'Coffret.Defaut_Disj_general',
        ]}
      />
      <AutomatonCabinetNodes
        status={status}
        nodes={[
          'Def_Com',
          'Electrificateur.Acquittement_Local',
          'Electrificateur.Defaut_Electrificateur',
          'Electrificateur.Erreur_Comm_Electrificateur',
          'Electrificateur.Etat_Electrificateur',
          'Electrificateur.Info_Acquittement_Local',
          'Electrificateur.INIT',
          'Electrificateur.Sectionneur_Arret_Electrificateur',
        ]}
      />
      <VStack
        paddingX={9}
        paddingY={4}
        backgroundColor="neutral.700"
        gap={1}
        width="100%"
        height="100%"
        alignItems="start"
      >
        <Text paddingY="11px" fontWeight="medium" color="neutral.200" textTransform="uppercase">
          <FormattedMessage id="components.monitoring.sensorDetail.global.automaton.segments" />
        </Text>
        <For each={configuration.segments}>
          {(segment) => {
            const segmentStatus = status?.subSensorStatuses.find((sub) => sub.uniqueKey === segment.uniqueKey);
            return (
              <HStack paddingY="7px" width="100%" gap={6}>
                <Text color="neutral.300">{segment.name}</Text>
                <HStack gap={1} marginLeft="auto">
                  <Text fontSize="md">
                    {segmentStatus ? <FormattedMessage id={`sensors.status.${segmentStatus.healthStatus}`} /> : '-'}
                  </Text>
                  <Icon
                    boxSize="16px"
                    color={
                      segmentStatus
                        ? statusColorFromSensorStatus[segmentStatus.healthStatus]
                        : hexColor[ColorLabelEnum.GRAY]
                    }
                    as={IcColorStatusSActive}
                  />
                </HStack>
              </HStack>
            );
          }}
        </For>
      </VStack>
    </Grid>
  );
}
